/**
 * Unfoldings-per-period line chart, shared by Weekly and Monthly Insight.
 *
 * Generalized from the original 7-day `RhythmChart`: geometry is derived from
 * `points.length` rather than hardcoded to a week, so 7 weekday points and
 * 28–31 day-of-month points use the same code path. With 7 points the output is
 * pixel-identical to the previous implementation (x = (i + 0.5) * 100).
 *
 * Illustrative, not analytical.
 */

export interface TrendPoint {
  label: string;
  count: number;
}

/**
 * Catmull-Rom through the points, expressed as cubic beziers — a gentle curve
 * rather than the hard elbows a polyline would give.
 */
export function smoothPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
  }
  return d;
}

const VIEW_W = 700;
const TOP = 18;
const BOTTOM = 96;
const BASELINE = 112;

export function TrendChart({
  points,
  ariaLabel,
  showLabel,
}: {
  points: TrendPoint[];
  ariaLabel: string;
  /** Which points print a label under the axis. Defaults to all of them —
   * a month passes `(i) => i % 7 === 0` so 31 labels don't crowd the axis. */
  showLabel?: (index: number) => boolean;
}) {
  if (points.length === 0) return null;

  const n = points.length;
  const step = VIEW_W / n;
  const max = Math.max(1, ...points.map((p) => p.count));
  const coords = points.map((p, i) => ({
    x: (i + 0.5) * step,
    y: BOTTOM - (p.count / max) * (BOTTOM - TOP),
  }));

  const line = smoothPath(coords);
  const area = `${line} L ${coords[n - 1].x} ${BASELINE} L ${coords[0].x} ${BASELINE} Z`;

  // A month's worth of markers needs to sit lighter than a week's, or the line
  // disappears under its own dots.
  const dense = n > 10;
  const strokeWidth = dense ? 2 : 2.5;
  const rOn = dense ? 3 : 5;
  const rOff = dense ? 1.75 : 3.5;

  return (
    <div>
      <svg viewBox={`0 0 ${VIEW_W} 124`} className="w-full" role="img" aria-label={ariaLabel}>
        <line
          x1={coords[0].x}
          y1={BASELINE}
          x2={coords[n - 1].x}
          y2={BASELINE}
          stroke="hsl(var(--foreground))"
          strokeOpacity="0.08"
          strokeWidth="1.5"
        />
        <path d={area} fill="hsl(var(--primary))" fillOpacity="0.07" />
        <path
          d={line}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeOpacity="0.75"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {coords.map((c, i) => (
          <circle
            key={i}
            cx={c.x}
            cy={c.y}
            r={points[i].count > 0 ? rOn : rOff}
            fill={points[i].count > 0 ? "hsl(var(--primary))" : "hsl(var(--background))"}
            stroke="hsl(var(--primary))"
            strokeOpacity={points[i].count > 0 ? 1 : 0.35}
            strokeWidth="2"
          />
        ))}
      </svg>
      {/* Labels are positioned at the same x-centres as the markers, so a sparse
          month axis still lines up with the days it names. */}
      <div className="relative h-4">
        {points.map((p, i) =>
          (showLabel ? showLabel(i) : true) ? (
            <span
              key={i}
              className="absolute -translate-x-1/2 text-[11px] leading-4 text-muted-foreground/70"
              style={{ left: `${((i + 0.5) / n) * 100}%` }}
            >
              {p.label}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}
